import IApiResponse from 'interfaces/apiResponse';
import IUserToken from 'interfaces/tokens/userToken';
import * as Rx from 'rxjs';
import * as RxOp from 'rxjs-operators';
import { ACCOUNTS_ENV, ACCOUNTS_PARTNER } from 'settings';

import apiService, { ApiService } from './api';
import tokenService, { TokenService } from './token';
import userService from 'services/userService';

export class AuthService {
  private user$ = new Rx.Observable<IUserToken>(null);

  constructor(private api: ApiService, private tokenService: TokenService) {
    this.api = apiService;
    this.tokenService = tokenService;

    this.user$ = this.tokenService.getAccessToken().pipe(
      RxOp.map(token => {
        if (!token) return null;

        const user = this.tokenService.decode<IUserToken>(token);
        if (!user) return null;

        userService.setUser(user);
        return user;
      }),
      RxOp.catchError(() => Rx.of(null)),
    );
  }

  public handleErrorMessage(err: any) {
    return this.api.handleErrorMessage(err);
  }

  public loginAccounts(acpoToken?: string) {
    const storedTokens = document.cookie.split('; ').find(row => row.startsWith('authToken'));

    if (!storedTokens) {
      const newLogin: Rx.Observable<string> = (window as any).Eduzz.Accounts.login(ACCOUNTS_PARTNER, ACCOUNTS_ENV);

      return this.parseAuth(
        newLogin.pipe(
          RxOp.switchMap(token => apiService.post('/auth_v2/login', { token, acpoToken })),
          RxOp.map(response => response?.data),
        ),
      );
    }

    try {
      const refreshToken = JSON.parse(storedTokens.split('=')[1]).jwt_refresh_token;
      this.parseAuth(
        apiService
          .post('/auth_v2/refresh', { jwt_refresh_token: refreshToken })
          .pipe(RxOp.map(response => response?.data)),
        () => this.handleRefreshError(acpoToken),
      );
    } catch {
      this.handleRefreshError(acpoToken);
    }
  }

  private handleRefreshError(acpoToken?: string): void {
    try {
      this.tokenService.clearToken();
    } catch {
      // noop
    }

    this.loginAccounts(acpoToken);
  }

  private parseAuth(step: Rx.Observable<any>, onError?: (err: any) => void) {
    step.subscribe({
      next: ({ jwt_token, jwt_refresh_token }) => {
        this.tokenService.setTokens({ jwt_token, jwt_refresh_token });
        localStorage.removeItem('authTokenAcpo');
        window.history.pushState(null, null, '/');
      },
      error: err => {
        if (onError) {
          onError(err);
          return;
        }
      },
    });
  }

  public login(data: any): Rx.Observable<IUserToken> {
    return this.api.post('/user/login', data).pipe(RxOp.map(res => res.data[0]));
  }

  public register(data: any): Rx.Observable<any> {
    return this.api.post('/user/register', data);
  }

  public getProfile(): Rx.Observable<IApiResponse<IUserToken>> {
    return this.api.get('/user/profile');
  }

  public logout(): void {
    tokenService.setTokenAcpo(null);
    tokenService.setTokens(null);
    (window as any).Eduzz.Accounts.logout({
      ...ACCOUNTS_ENV,
      redirectTo: window.location.origin,
    });
  }

  public sendResetPassword(email: string): Rx.Observable<void> {
    return this.api.post('/auth/send-reset', { email });
  }

  public resetPassword(token: string, password: string): Rx.Observable<void> {
    return this.api.post('/auth/reset-password', { token, password });
  }

  public changePassword(currentPassword: string, newPassword: string): Rx.Observable<void> {
    return this.api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  }

  public getUser(): Rx.Observable<IUserToken> {
    return this.user$;
  }

  public canAccess(...roles: string[]): Rx.Observable<boolean> {
    return this.getUser().pipe(
      RxOp.map(user => {
        if (!user) return false;

        if (!roles || roles.length === 0) return true;

        return true;
      }),
    );
  }

  public isAuthenticated(): Rx.Observable<boolean> {
    return this.getUser().pipe(RxOp.map(user => !!user));
  }
}

const authService = new AuthService(apiService, tokenService);
export default authService;
